import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../store/useStore';
import {
    LayoutDashboard,
    ShoppingCart,
    Package,
    History as HistoryIcon,
    LogOut,
    Menu,
    X,
    Users,
    CreditCard,
    BarChart3,
    Settings,
    DollarSign,
    Gift,
    MessageSquare,
    Crown,
    AlertTriangle
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useEntitlements } from '../hooks/useEntitlements';

interface LayoutProps {
    children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useStore((state) => state.user);
    const logout = useStore((state) => state.logout);
    const settings = useStore((state) => state.settings);
    const updateUserActivity = useStore((state) => state.updateUserActivity);
    const fetchUsers = useStore((state) => state.fetchUsers);
    const license = useStore((state) => state.license);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
    const { hasModuleAccess, isPremiumModuleWarning, isReadOnly } = useEntitlements();

    // Heartbeat for "Online" status
    useEffect(() => {
        if (!user) return;

        // Initial "I am here"
        updateUserActivity('Navegando: ' + location.pathname);
        fetchUsers(); // Initial fetch to see others

        // Heartbeat every 2 minutes (updates my status AND fetches others)
        const interval = setInterval(() => {
            updateUserActivity('Activo (Heartbeat)');
            fetchUsers();
        }, 30000); // 30 seconds for faster updates (user asked for visibility)

        return () => clearInterval(interval);
    }, [user?.id, location.pathname, updateUserActivity, fetchUsers]);

    const hasPermission = (requiredPermission: string) => {
        if (!user) return false;
        if (user.role === 'superadmin') return true; // Explicit superadmin check
        if (user.role === 'admin') return true; // Fallback superset
        if (user.permissions?.includes('*')) return true;
        return user.permissions?.includes(requiredPermission as any);
    };

    const links = [
        { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, permission: 'reports:view', moduleId: 'reports' },
        { name: 'Ventas', href: '/sales', icon: ShoppingCart, permission: 'sales:create', moduleId: 'pos' },
        { name: 'Historial', href: '/history', icon: HistoryIcon, permission: 'sales:read', moduleId: 'audit-history' },
        { name: 'Inventario', href: '/inventory', icon: Package, permission: 'products:read', moduleId: 'inventory' },
        { name: 'Compras', href: '/purchases', icon: CreditCard, permission: 'products:manage', moduleId: 'purchases' },
        { name: 'Gastos', href: '/expenses', icon: DollarSign, permission: 'expenses:manage', moduleId: 'expenses' },
        { name: 'Monedero', href: '/loyalty', icon: Gift, permission: 'clients:read', moduleId: 'loyalty' },
        { name: 'Flujo de Caja', href: '/cash-flow', icon: DollarSign, permission: 'cashflow:read', moduleId: 'cash-flow' },
        { name: 'Clientes', href: '/clients', icon: Users, permission: 'clients:read', moduleId: 'clients' },
        { name: 'Usuarios', href: '/users', icon: Users, permission: 'users:manage', moduleId: 'users' },
        { name: 'Roles', href: '/roles', icon: Users, permission: 'users:manage', moduleId: 'roles' },
        { name: 'Reportes', href: '/reports', icon: BarChart3, permission: 'reports:view', moduleId: 'reports' },
        { name: 'Suscripción', href: '/subscription', icon: Settings, permission: null },
        { name: 'Leads', href: '/leads', icon: MessageSquare, permission: 'leads:manage', restrictedTo: ['superadmin'] },
        { name: 'Planes', href: '/subscription-plans', icon: CreditCard, permission: 'plans:manage', restrictedTo: ['superadmin'] },
        { name: 'Configuración', href: '/settings', icon: Settings, permission: 'settings:manage' },
    ];

    const filteredLinks = links.filter(link => {
        if (link.restrictedTo && user) {
            if (!link.restrictedTo.includes(user.role as any)) return false;
        }
        if (link.moduleId && !hasModuleAccess(link.moduleId)) {
            return false;
        }
        if (!link.permission) return true;
        return hasPermission(link.permission);
    });

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    return (
        <div className="flex flex-col md:flex-row bg-slate-50 min-h-screen font-sans">
            {/* Mobile Header */}
            <div className="md:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between sticky top-0 z-50">
                <div className="flex items-center gap-2">
                    {settings.logo ? (
                        <img src={settings.logo} alt="Logo" className="w-8 h-8 object-contain" />
                    ) : (
                        <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-white" />
                        </div>
                    )}
                    <span className="font-black text-slate-800 tracking-tight">{settings.companyName || 'Inventory App SaaS'}</span>
                </div>
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {/* Sidebar - HIDDEN on Checkout Status Page */}
            {!location.pathname.includes('/checkout/status') && (
                <aside className={cn(
                    "fixed inset-y-0 left-0 bg-white border-r border-slate-200 w-64 z-50 transform transition-transform duration-300 md:relative md:translate-x-0",
                    isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
                )}>
                    <div className="h-full flex flex-col overflow-y-auto">
                        {/* Sidebar Header */}
                        <div className="p-6">
                            <div className="flex items-center gap-3 mb-8">
                                {settings.logo ? (
                                    <img src={settings.logo} alt="Logo" className="w-10 h-10 object-contain" />
                                ) : (
                                    <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center shadow-lg shadow-primary-500/30">
                                        <Package className="w-6 h-6 text-white" />
                                    </div>
                                )}
                                <div>
                                    <h1 className="font-black text-slate-800 leading-none tracking-tight">
                                        {settings.companyName || 'Inventory App SaaS'}
                                    </h1>
                                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">
                                        {settings.slogan || 'SaaS Management'}
                                    </p>
                                </div>
                            </div>

                            {/* User Info */}
                            <div
                                onClick={() => setIsProfileModalOpen(true)}
                                className="bg-slate-50 rounded-xl p-3 mb-6 flex items-center gap-3 cursor-pointer hover:bg-slate-100 transition-colors"
                            >
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Avatar" className="w-8 h-8 rounded-full border border-slate-200" />
                                ) : (
                                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-700 font-bold text-xs border border-primary-200">
                                        {user?.name.charAt(0)}
                                    </div>
                                )}
                                <div className="flex-1 overflow-hidden">
                                    <p className="text-xs font-bold text-slate-800 truncate">{user?.name}</p>
                                    <p className="text-[10px] text-slate-500 uppercase font-medium">
                                        {user?.role === 'admin' ? 'Administrador' :
                                            user?.role === 'superadmin' ? 'Super Admin' :
                                                user?.role === 'manager' ? 'Gerente' : 'Vendedor'}
                                    </p>
                                </div>
                            </div>

                            {/* Navigation */}
                            <nav className="space-y-1">
                                {filteredLinks.map((link) => {
                                    const Icon = link.icon;
                                    const isActive = location.pathname === link.href;
                                    return (
                                        <button
                                            key={link.name}
                                            onClick={() => {
                                                navigate(link.href);
                                                setIsMobileMenuOpen(false);
                                            }}
                                            className={cn(
                                                "w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200",
                                                isActive
                                                    ? "bg-primary-600 text-white shadow-lg shadow-primary-500/20"
                                                    : "text-slate-500 hover:bg-slate-50 hover:text-slate-900"
                                            )}
                                        >
                                            <div className="flex items-center gap-3">
                                                <Icon className={cn("w-5 h-5", isActive ? "text-white" : "text-slate-400")} />
                                                {link.name}
                                            </div>
                                            {link.moduleId && isPremiumModuleWarning(link.moduleId) && (
                                                <span title="Módulo Premium - Transitorio por Trial">
                                                    <Crown className={cn("w-4 h-4 ml-2", isActive ? "text-amber-300" : "text-amber-500")} />
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </nav>
                        </div>

                        {/* Bottom actions */}
                        <div className="mt-auto p-6 border-t border-slate-100">
                            <button
                                onClick={handleLogout}
                                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold text-red-500 hover:bg-red-50 transition-all duration-200 group"
                            >
                                <LogOut className="w-5 h-5 text-red-400 group-hover:text-red-500" />
                                Cerrar Sesión
                            </button>
                        </div>
                    </div>
                </aside>
            )}

            {/* Main Content */}
            <main className="flex-1 min-w-0 flex flex-col relative h-screen overflow-y-auto">
                {isReadOnly && !location.pathname.includes('/subscription') && !location.pathname.includes('/checkout/status') && (
                    <div className="bg-red-500 text-white px-4 py-3 border-b-2 border-red-600 shadow-md z-40 flex items-center justify-center gap-3 flex-wrap animate-fade-in-down sticky top-0">
                        <AlertTriangle className="w-5 h-5 shrink-0" />
                        <span className="font-bold text-sm">¡Tu suscripción está vencida!</span>
                        <span className="text-sm opacity-90 hidden sm:inline">Tu cuenta se encuentra en modo de solo lectura.</span>
                        <button
                            onClick={() => navigate('/subscription')}
                            className="bg-white text-red-600 font-black px-4 py-1.5 rounded-full text-xs hover:bg-red-50 transition-colors shadow-sm whitespace-nowrap hover:scale-105 transform duration-200"
                        >
                            Renovar Ahora
                        </button>
                    </div>
                )}
                <div className="flex-1 p-4 md:p-8 relative">
                    {children}
                </div>
            </main>

            {/* Overlay for mobile menu */}
            {isMobileMenuOpen && (
                <div
                    className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 md:hidden"
                    onClick={() => setIsMobileMenuOpen(false)}
                />
            )}

            {/* Profile Modal (ID Card) */}
            {isProfileModalOpen && (
                <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setIsProfileModalOpen(false)}>
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden transform transition-all scale-100" onClick={e => e.stopPropagation()}>
                        {/* ID Header */}
                        <div className="bg-gradient-to-r from-primary-600 to-primary-800 p-6 text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-full opacity-10 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')]"></div>
                            <button
                                onClick={() => setIsProfileModalOpen(false)}
                                className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <div className="w-24 h-24 mx-auto bg-white p-1 rounded-full shadow-lg mb-3">
                                {user?.avatar ? (
                                    <img src={user.avatar} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                    <div className="w-full h-full rounded-full bg-slate-100 flex items-center justify-center text-primary-600 font-bold text-3xl">
                                        {user?.name.charAt(0)}
                                    </div>
                                )}
                            </div>
                            <h2 className="text-xl font-bold text-white relative">{user?.name}</h2>
                            <span className="inline-block bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-full mt-2 relative">
                                {user?.role === 'admin' ? 'ADMINISTRADOR' :
                                    user?.role === 'superadmin' ? 'SUPER ADMIN' :
                                        user?.role === 'manager' ? 'GERENTE' : 'VENDEDOR'}
                            </span>
                        </div>

                        {/* ID Body */}
                        <div className="p-6 space-y-4">
                            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="bg-primary-100 p-2 rounded-lg text-primary-600">
                                    <Users className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-400 font-bold uppercase">Correo Electrónico</p>
                                    <p className="text-sm font-semibold text-slate-700 truncate w-48">{user?.email}</p>
                                </div>
                            </div>

                            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                <div className="bg-blue-100 p-2 rounded-lg text-blue-600">
                                    <Settings className="w-5 h-5" />
                                </div>
                                <div className="flex-1">
                                    <p className="text-xs text-slate-400 font-bold uppercase">Empresa</p>
                                    <p className="text-sm font-semibold text-slate-700 text-uppercase">{settings.companyName || 'Inventory App SaaS'}</p>
                                </div>
                            </div>

                            {/* License Status */}
                            {license && (
                                <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border border-slate-100">
                                    <div className={`p-2 rounded-lg ${license.status === 'trialing' ? 'bg-blue-100 text-blue-600' : license.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                        <CreditCard className="w-5 h-5" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs text-slate-400 font-bold uppercase">
                                            {(license as any).planName || 'Plan Básico'}
                                        </p>
                                        <div className="flex justify-between items-center">
                                            <p className="text-sm font-semibold text-slate-700">
                                                {(() => {
                                                    if (user?.role === 'superadmin') return 'Membresía Vitalicia (100 Años)';
                                                    if (license.status === 'trialing') {
                                                        if (!license.expirationDate) return 'Prueba Gratis — 14 días';
                                                        const days = Math.ceil((new Date(license.expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                                        return days > 0 ? `Prueba — ${days} días` : 'Prueba Expirada';
                                                    }
                                                    if (!license.expirationDate) return 'Sin Expiración';
                                                    const days = Math.ceil((new Date(license.expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                                    return days > 0 ? `${days} Días Restantes` : 'Vencida';
                                                })()}
                                            </p>
                                            {(() => {
                                                if (user?.role === 'superadmin') return null;
                                                if (!license.expirationDate) return null;
                                                const days = Math.ceil((new Date(license.expirationDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                                                if (days <= 7) {
                                                    return (
                                                        <button
                                                            onClick={() => navigate('/subscription')}
                                                            className="text-[10px] bg-red-600 text-white px-2 py-1 rounded-md font-bold animate-pulse hover:bg-red-700"
                                                        >
                                                            PAGAR
                                                        </button>
                                                    );
                                                }
                                                return null;
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="pt-2 text-center">
                                <p className="text-xs text-slate-300 font-mono">{settings.slogan || 'SISTEMA DE INVENTARIO'}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
